import { Component, OnInit, ViewChild, ElementRef, ChangeDetectorRef } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule, ReactiveFormsModule, FormBuilder, FormGroup, Validators, FormArray, AbstractControl } from '@angular/forms';
import { TagInputModule } from 'ngx-chips';
import { News } from '../../core/models/news.interface';
import { Storage, ref, uploadBytesResumable, getDownloadURL } from '@angular/fire/storage';
import { Firestore, collection, addDoc, updateDoc, doc, getDocs, query, orderBy, where, Timestamp, getDoc } from '@angular/fire/firestore';
import imageCompression from 'browser-image-compression';
import { DomSanitizer, SafeUrl, SafeHtml } from '@angular/platform-browser';
import { NewsViewerComponent } from '../news-viewer/news-viewer.component';
import { AuthService } from '../../core/services/auth.service';
import { SafePipe } from '../../shared/pipes/safe.pipe';
import { NewsService } from '../../core/services/news.service';
import { CategoryService, Category } from '../../core/services/category.service';
import { Router } from '@angular/router';
import { EditorModule } from '@tinymce/tinymce-angular';

@Component({
  selector: 'app-news-editor',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    ReactiveFormsModule,
    TagInputModule,
    NewsViewerComponent,
    SafePipe,
    EditorModule
  ],
  templateUrl: './news-editor.component.html',
  styleUrls: ['./news-editor.component.scss']
})
export class NewsEditorComponent implements OnInit {
  @ViewChild('imageInput') imageInput!: ElementRef<HTMLInputElement>;
  @ViewChild('contentImageInput') contentImageInput!: ElementRef<HTMLInputElement>;

  articleForm: FormGroup;
  currentArticle: News | null = null;
  private editorStateBeforePreview: any = null; // <-- Añadir variable para guardar estado
  articles: News[] = [];
  filteredArticles: News[] = [];
  searchTerm: string = '';
  isUploading: boolean = false;
  uploadProgress: number = 0;
  uploadingImageType: 'main' | 'paragraph' | null = null;
  selectedPosition: number | null = null;
  notification: { type: 'success' | 'danger'; message: string } | null = null;
  showLoadingMessage: boolean = false;
  mensaje: string = '';
  tipoMensaje: string = '';

  // Variables para el editor de texto
  selectedParagraphIndex: number | null = null;
  showFormatToolbar: boolean = false;
  showLinkDialog: boolean = false;
  linkUrl: string = '';
  selectedText: string = '';
  private selectedRange: Range | null = null;

  categories: Category[] = [];
  availableCategories: Category[] = [];

  previewMode = false;
  previewArticle: News | null = null;

  currentUser: any | null = null;

  // Configuración de TinyMCE
  editorConfig = {
    height: 300,
    menubar: false,
    language: 'es',
    language_url: 'https://cdn.tiny.cloud/1/8dg3l0xydd4tl812e3mbj0x9mrgmll4tzovl9buxql2hfucz/tinymce/7/langs/es.js',
    branding: false, // <-- Añadido para ocultar el logo
    plugins: [
      'advlist', 'autolink', 'lists', 'link', 'image', 'charmap', 'preview',
      'anchor', 'searchreplace', 'visualblocks', 'code', 'fullscreen',
      'insertdatetime', 'media', 'table', 'help', 'wordcount'
    ],
    toolbar: 'undo redo | formatselect ' +
      'removeformat link | ' +
      'bold italic backcolor | alignleft aligncenter ' +
      'alignright alignjustify | bullist numlist outdent indent | ',
    content_style: 'body { font-family:Helvetica,Arial,sans-serif; font-size:15px }',
    
  };

  constructor(
    private fb: FormBuilder,
    private storage: Storage,
    private firestore: Firestore,
    private sanitizer: DomSanitizer,
    private authService: AuthService,
    private cdr: ChangeDetectorRef,
    private newsService: NewsService,
    private categoryService: CategoryService,
    private router: Router
  ) {
    this.articleForm = this.createArticleForm();
    // Obtener el usuario inicial
    this.currentUser = this.authService.getCurrentUser();
    console.log('Usuario inicial en constructor:', this.currentUser);
  }

  async ngOnInit() {
    // Esperar a que el servicio de autenticación esté inicializado
    await this.authService.waitForUserInitialization();
    
    // Suscribirse a cambios futuros del usuario
    this.authService.currentUser$.subscribe(user => {
      if (user) {
        console.log('Usuario actualizado:', user);
        this.currentUser = user;
        this.loadAvailableCategories();
      }
    });
    
    this.loadArticles();
    this.setupStatusChangeSubscription(); // <-- Llamar a la configuración del validador
    this.loadAvailableCategories();
  }

  private createArticleForm(): FormGroup {
    return this.fb.group({
      title: ['', [Validators.required, Validators.minLength(5)]],
      subtitle: [''],
      imageUrl: [''],
      imagePreview: [''],
      imageCaption: [''],
      author: [''], // Campo opcional para el autor/redactor
      category: ['', Validators.required],
      subcategory: [''],
      featured: [false],
      content: [''], // Este campo se manejará manualmente o se eliminará si solo usamos párrafos
      status: ['draft'], // Valor inicial por defecto
      tags: [[]],
      paragraphs: this.fb.array([]),
      scheduledPublishDate: [null],
      publishDate: [null, Validators.required] // <-- Nuevo campo obligatorio
    });
  }

  /**
   * Carga las categorías disponibles según el rol del usuario
   */
  private loadAvailableCategories(): void {
    if (!this.currentUser) {
      console.log('No hay usuario autenticado, no se pueden cargar categorías');
      return;
    }

    console.log('Cargando categorías para usuario:', this.currentUser.role);
    
    // Obtener todas las categorías
    this.categories = this.categoryService.getAllCategories();
    
    // Obtener categorías disponibles según el rol
    this.availableCategories = this.categoryService.getCategoriesForRole(this.currentUser.role);
    
    console.log('Categorías disponibles:', this.availableCategories.map(cat => cat.name));
    
    // Si el usuario actual no tiene categorías disponibles, mostrar mensaje
    if (this.availableCategories.length === 0) {
      console.warn('El usuario no tiene categorías disponibles');
      this.mostrarMensaje('No tienes permisos para crear artículos en ninguna categoría', 'danger');
    }
  }

  // <-- Añadir método para configurar la suscripción al cambio de estado
  private setupStatusChangeSubscription(): void {
    this.articleForm.get('status')?.valueChanges.subscribe(status => {
      const scheduledDateControl = this.articleForm.get('scheduledPublishDate');
      const publishDateControl = this.articleForm.get('publishDate');
      
      if (status === 'scheduled') {
        scheduledDateControl?.setValidators(Validators.required);
        publishDateControl?.clearValidators();
        publishDateControl?.reset(); // Limpiar publishDate cuando es programado
      } else {
        scheduledDateControl?.clearValidators();
        publishDateControl?.setValidators(Validators.required); // publishDate requerido para otros estados
      }
      scheduledDateControl?.updateValueAndValidity();
      publishDateControl?.updateValueAndValidity();
    });
  }

  get paragraphsArray() {
    return this.articleForm.get('paragraphs') as FormArray;
  }

  addParagraph(paragraph?: any) {
    this.paragraphsArray.push(this.createParagraphGroup(paragraph));
  }

  private createParagraphGroup(paragraph?: any): FormGroup {
    // Log para ver qué datos recibe esta función
    console.log('createParagraphGroup receiving:', JSON.stringify(paragraph, null, 2));

    const group = this.fb.group({
      title: [paragraph?.title || '', Validators.maxLength(150)],
      content: [paragraph?.content || '', Validators.required],
      image: [paragraph?.image || null],
      imagePreview: [paragraph?.image || null],
      imageCaption: [paragraph?.imageCaption || '', Validators.maxLength(200)],
      urlVideo: [paragraph?.urlVideo || ''],
      pieDeVideo: [paragraph?.pieDeVideo || '']
    });

    // Log para ver el valor inicial del grupo creado
    console.log('createParagraphGroup created group with value:', JSON.stringify(group.value, null, 2));

    return group;
  }

  removeParagraph(index: number) {
    this.paragraphsArray.removeAt(index);
  }

  cancelEdit() {
    this.currentArticle = null;
    this.articleForm.reset({
      status: 'draft', // Restablecer estado a 'draft' por defecto
      featured: false,
      tags: [],
      paragraphs: [],
      scheduledPublishDate: null, // Asegurar que se limpia
      publishDate: null // Asegurar que se limpia
    });
    this.paragraphsArray.clear(); // Limpiar el FormArray explícitamente
    this.clearNotification();
    // Salir del modo vista previa si está activo
    if (this.previewMode) {
      this.previewMode = false;
      this.previewArticle = null;
    }
  }

  async saveArticle() {
    // Forzar actualización de validadores antes de verificar
    this.articleForm.get('scheduledPublishDate')?.updateValueAndValidity();
    this.articleForm.get('publishDate')?.updateValueAndValidity(); // Forzar validación de publishDate

    // Marcar todos los campos como 'touched' para mostrar errores de validación
    Object.values(this.articleForm.controls).forEach(control => {
      control.markAsTouched();
    });

    // Marcar los controles dentro de cada grupo de párrafo como 'touched'
    this.paragraphsArray.controls.forEach((group: AbstractControl) => {
      if (group instanceof FormGroup) { // <-- Añadir verificación y type casting implícito
        Object.values(group.controls).forEach(control => {
          control.markAsTouched();
        });
      }
    });

    console.log('Formulario antes de guardar:', this.articleForm.value);
    console.log('Estado de validez del formulario:', this.articleForm.status);

    if (this.articleForm.invalid) {
      if (this.articleForm.get('category')?.invalid) {
        this.notification = { type: 'danger', message: 'Por favor, selecciona una categoría.' }; // <-- Corregir a this.notification
      } else if (this.articleForm.get('title')?.invalid) {
        this.notification = { type: 'danger', message: 'El título es requerido y debe tener al menos 5 caracteres.' }; // <-- Corregir a this.notification
      } else {
        // Mensaje genérico si otro campo es inválido
        this.notification = { type: 'danger', message: 'Por favor, completa todos los campos requeridos marcados en rojo.' }; // <-- Corregir a this.notification
      }
      this.cdr.detectChanges(); // Forzar detección de cambios para mostrar notificación
      return; // Detener si el formulario es inválido
    }

    // Validar que el usuario puede gestionar la categoría seleccionada
    const selectedCategory = this.articleForm.get('category')?.value;
    if (selectedCategory && this.currentUser) {
      const canManageCategory = this.categoryService.canUserManageCategory(this.currentUser.role, selectedCategory);
      if (!canManageCategory) {
        this.notification = { 
          type: 'danger', 
          message: `No tienes permisos para crear artículos en la categoría "${selectedCategory}".` 
        };
        this.cdr.detectChanges();
        return;
      }
    }

    this.mostrarMensaje('Guardando artículo...', 'info');

    try {
      const formValue = this.articleForm.value;
      let publishDate: Date | null = null;
      let scheduledTimestamp: Timestamp | null = null; // <-- Usar Timestamp
      let publishDateInput: string | null = this.articleForm.get('publishDate')?.value;

      // Determinar fechas basado en el estado
      if (formValue.status === 'published') {
        // Usar la fecha elegida por el usuario, o la existente, o la actual
        if (publishDateInput) {
          publishDate = new Date(publishDateInput);
        } else if (this.currentArticle?.publishDate) {
          publishDate = this.currentArticle.publishDate instanceof Date ? this.currentArticle.publishDate : new Date(this.currentArticle.publishDate);
        } else {
          publishDate = new Date();
        }
        scheduledTimestamp = null; // Asegurarse que no quede fecha programada
      } else if (formValue.status === 'scheduled') {
        if (formValue.scheduledPublishDate) {
          // Convertir string de datetime-local a Date, luego a Timestamp
          scheduledTimestamp = Timestamp.fromDate(new Date(formValue.scheduledPublishDate));
          publishDate = null; // No hay fecha de publicación inmediata
        } else {
          // Esto no debería ocurrir si la validación funciona, pero por seguridad
          this.mostrarMensaje('Error: Falta la fecha de programación.', 'error');
          return;
        }
      } else { // draft or archived
        publishDate = null;
        scheduledTimestamp = null;
      }

      // Asegurarse de que los tags sean un array
      const tags = Array.isArray(formValue.tags) ? formValue.tags : [];

      // Obtener el usuario actual
      const currentUser = this.authService.getCurrentUser();
      if (!currentUser) {
        this.mostrarMensaje('Error: No hay un editor autenticado', 'error');
        return;
      }

      const articleData: Partial<News> = {
        title: formValue.title,
        subtitle: formValue.subtitle || '',
        category: formValue.category,
        subcategory: formValue.subcategory || '',
        featured: formValue.featured || false,
        imageUrl: formValue.imageUrl || '',
        imageCaption: formValue.imageCaption || null,
        status: formValue.status || 'draft',
        tags: tags, // Usar la variable tags asegurada como array
        author: formValue.author || currentUser.name, // Usar autor del formulario o usuario logueado como fallback
        authorId: currentUser.id, // <-- Corregido: Usar 'id' en lugar de 'uid'
        lastModified: new Date(),
        paragraphs: formValue.paragraphs.map((p: any) => ({ // Asegurar que se mapean párrafos
          title: p.title || '', // <-- AÑADIR title
          content: p.content || '',
          image: p.image || null, // <-- CORREGIR a p.image y default a null
          imageCaption: p.imageCaption || '',
          urlVideo: p.urlVideo || '',
          pieDeVideo: p.pieDeVideo || ''
        })),
        // Incluir slug (se genera en newsService si es nuevo)
        // Opcional: mantener slug existente si se edita
        slug: this.currentArticle?.slug || '', 
        publishDate: publishDate, // <-- Asignar fecha de publicación
        scheduledPublishDate: scheduledTimestamp, // <-- Asignar fecha programada
      };

      const articlesRef = collection(this.firestore, 'articles');

      if (this.currentArticle?.id) {
        const articleRef = doc(this.firestore, 'articles', this.currentArticle.id);
        await updateDoc(articleRef, articleData);
        this.mostrarMensaje('Artículo actualizado exitosamente', 'success');
      } else {
        // Es un artículo NUEVO: Generar el slug aquí
        articleData.slug = this.generateSlug(formValue.title); 
        articleData.createdAt = new Date(); // <-- Guardar fecha de creación real solo al crear
        await addDoc(articlesRef, articleData);
        this.mostrarMensaje('Artículo creado exitosamente', 'success');
      }

      await this.loadArticles();
      this.createNewArticle();
    } catch (error) {
      console.error('Error al guardar el artículo:', error);
      this.mostrarMensaje('Error al guardar el artículo', 'error');
    }
  }

  moveParagraph(index: number, direction: 'up' | 'down') {
    const paragraphs = this.paragraphsArray;
    if (direction === 'up' && index > 0) {
      const temp = paragraphs.at(index - 1)?.value;
      paragraphs.at(index - 1)?.patchValue(paragraphs.at(index)?.value);
      paragraphs.at(index)?.patchValue(temp);
    } else if (direction === 'down' && index < paragraphs.length - 1) {
      const temp = paragraphs.at(index + 1)?.value;
      paragraphs.at(index + 1)?.patchValue(paragraphs.at(index)?.value);
      paragraphs.at(index)?.patchValue(temp);
    }
  }

  selectPosition(index: number) {
    this.selectedPosition = index;
  }

  removeImage(index: number) {
    const paragraph = this.paragraphsArray.at(index);
    if (paragraph) {
      paragraph.patchValue({ imageUrl: '', imagePreview: '', imageCaption: '' });
    }
  }

  triggerImageUpload() {
    this.imageInput.nativeElement.click();
  }

  triggerContentImageUpload(index: number) {
    this.selectPosition(index);
    this.contentImageInput.nativeElement.click();
  }

  clearNotification() {
    this.notification = null;
  }

  mostrarMensaje(mensaje: string, tipo: string) {
    this.mensaje = mensaje;
    this.tipoMensaje = tipo;
    setTimeout(() => {
      this.mensaje = '';
      this.tipoMensaje = '';
    }, 3000);
  }

  private generateSlug(text: string): string {
    return text
      .toString()
      .toLowerCase()
      .trim()
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '')
      .replace(/[^a-z0-9\s-]/g, '')
      .replace(/[\s-]+/g, '-')
      .replace(/^-+|-+$/g, '');
  }

  async loadArticles() {
    try {
      const articlesRef = collection(this.firestore, 'articles');
      const q = query(articlesRef, orderBy('lastModified', 'desc'));
      const querySnapshot = await getDocs(q);
      
      this.articles = querySnapshot.docs.map(doc => {
        const data = doc.data();
        return {
          id: doc.id,
          ...data,
          lastModified: data['lastModified']?.toDate() || new Date(),
          publishDate: data['publishDate']?.toDate() || null,
          createdAt: data['createdAt']?.toDate() || null // <-- Convertir createdAt
        } as News;
      });
      
      this.filterArticles();
    } catch (error) {
      console.error('Error loading articles:', error);
      this.mostrarMensaje('Error al cargar los artículos', 'error');
    }
  }

  filterArticles() {
    if (!this.searchTerm.trim()) {
      this.filteredArticles = [...this.articles];
    } else {
      const searchTermLower = this.searchTerm.toLowerCase();
      this.filteredArticles = this.articles.filter(article =>
        article.title.toLowerCase().includes(searchTermLower)
      );
    }
  }

  async selectArticle(article: News) {
    // Verificar si el usuario actual es el autor o es administrador
    if (this.currentUser?.role !== 'admin' && article.authorId !== this.currentUser?.id) {
      this.notification = {
        type: 'danger',
        message: 'No tienes permisos para editar este artículo. Solo el autor puede editarlo.'
      };
      return;
    }

    this.currentArticle = article;
    this.clearNotification();

    // Formatear la fecha programada si existe para el input datetime-local
    const scheduledDateValue = this.formatTimestampForInput(article.scheduledPublishDate);
    // Formatear publishDate para el input datetime-local
    let publishDateValue = null;
    if (article.publishDate) {
      const date = article.publishDate instanceof Date ? article.publishDate : new Date(article.publishDate);
      const year = date.getFullYear();
      const month = (date.getMonth() + 1).toString().padStart(2, '0');
      const day = date.getDate().toString().padStart(2, '0');
      const hours = date.getHours().toString().padStart(2, '0');
      const minutes = date.getMinutes().toString().padStart(2, '0');
      publishDateValue = `${year}-${month}-${day}T${hours}:${minutes}`;
    }

    // Resetear el array de párrafos antes de llenarlo
    this.paragraphsArray.clear(); 

    // Llenar el array de párrafos con los datos del artículo seleccionado
    if (article.paragraphs) {
      article.paragraphs.forEach(p => this.addParagraph(p));
    }

    // Cargar datos en el formulario
    this.articleForm.patchValue({
      title: article.title,
      subtitle: article.subtitle || '',
      imageUrl: article.imageUrl || '',
      imagePreview: article.imageUrl || '', // Para mostrar la imagen existente
      imageCaption: article.imageCaption || '',
      author: article.author || '', // Cargar el autor si existe
      category: article.category,
      featured: article.featured || false,
      status: article.status,
      tags: article.tags || [],
      scheduledPublishDate: scheduledDateValue, // <-- Cargar fecha formateada
      publishDate: publishDateValue // <-- Cargar publishDate
      // No patchear 'paragraphs' aquí, ya se llenó el FormArray
    });

    // Importante: Trigger status change logic in case the loaded article is 'scheduled'
    this.articleForm.get('status')?.updateValueAndValidity({ emitEvent: true });

    // Salir del modo vista previa si estaba activo
    this.previewMode = false;
    this.previewArticle = null;
  }

  createNewArticle() {
    this.currentArticle = null;
    // Crear un nuevo formulario para asegurar estado limpio
    this.articleForm = this.createArticleForm(); 
    
    // Inicializar el campo author con el usuario logueado
    if (this.currentUser) {
      this.articleForm.patchValue({
        author: this.currentUser.name
      });
    }
    
    this.paragraphsArray.clear();
    // Añadir un párrafo inicial si se desea
    // this.addParagraph(); 
    this.setupStatusChangeSubscription(); // <-- Reaplicar suscripción al nuevo form
    this.clearNotification();
    this.previewMode = false;
    this.previewArticle = null;
  }

  // <-- Añadir función helper para formatear Timestamp a string YYYY-MM-DDTHH:mm
  private formatTimestampForInput(timestamp: any): string | null {
    if (!timestamp || typeof timestamp.toDate !== 'function') {
        return null;
    }
    try {
      const date = timestamp.toDate();
      // Asegurarse que es una fecha válida
      if (isNaN(date.getTime())) {
        console.error("Invalid Date derived from Timestamp:", timestamp);
        return null;
      }
      const year = date.getFullYear();
      const month = (date.getMonth() + 1).toString().padStart(2, '0');
      const day = date.getDate().toString().padStart(2, '0');
      const hours = date.getHours().toString().padStart(2, '0');
      const minutes = date.getMinutes().toString().padStart(2, '0');
      return `${year}-${month}-${day}T${hours}:${minutes}`;
    } catch (error) {
      console.error("Error formatting Timestamp to Date string:", error);
      return null;
    }
  }

  async compressImage(file: File): Promise<File> {
    try {
      const options = {
        maxSizeMB: 1,
        maxWidthOrHeight: 1000,
        useWebWorker: true,
      };
      
      const compressedBlob = await imageCompression(file, options);
      const compressedFile = new File([compressedBlob], file.name, { type: 'image/jpeg' });
      
      return compressedFile;
    } catch (error) {
      console.error('Error compressing image:', error);
      throw error;
    }
  }

  async handleImageUpload(event: any, isParagraph: boolean = false, paragraphIndex?: number) {
    const file = event.target.files[0];
    if (file) {
      try {
        // Create preview
        const reader = new FileReader();
        reader.onload = (e: any) => {
          if (isParagraph && typeof paragraphIndex === 'number') {
            const paragraph = this.paragraphsArray.at(paragraphIndex);
            paragraph.patchValue({
              imagePreview: e.target.result
            });
          } else {
            this.articleForm.patchValue({
              imagePreview: e.target.result
            });
          }
        };
        reader.readAsDataURL(file);

        // Continue with your existing upload logic
        // ... your existing upload code ...
      } catch (error) {
        console.error('Error handling image upload:', error);
        this.notification = {
          type: 'danger',
          message: 'Error al cargar la imagen'
        };
      }
    }
  }

  async onImageSelected(event: any) {
    const file = event.target.files[0];
    if (file) {
      try {
        // Mostrar mensaje de carga
        this.mostrarMensaje('Subiendo imagen principal...', 'info');
        this.isUploading = true;
        this.uploadingImageType = 'main';
        this.uploadProgress = 0;
        
        // Create preview immediately
        const reader = new FileReader();
        reader.onload = (e: any) => {
          this.articleForm.patchValue({
            imagePreview: e.target.result
          });
        };
        reader.readAsDataURL(file);

        // **Log de autenticación**
        console.log('[onImageSelected] Verificando autenticación antes de subir:');
        this.authService.currentUser$.subscribe(user => {
          console.log('  currentUser$ (observable):', user);
        });
        console.log('  currentUser (propiedad directa):', this.currentUser);
        // ******

        // Upload image to Firebase
        const compressedFile = await this.compressImage(file);
        const path = `articles/${Date.now()}_${compressedFile.name}`;
        const storageRef = ref(this.storage, path);
        
        try {
          // Crear la tarea de subida
          const uploadTask = uploadBytesResumable(storageRef, compressedFile);

          // Monitorear el progreso
          uploadTask.on('state_changed', 
            (snapshot) => {
              const progress = (snapshot.bytesTransferred / snapshot.totalBytes) * 100;
              this.uploadProgress = Math.round(progress);
            },
            (error) => { 
              console.error('Error durante la subida (listener):', error);
              this.notification = {
                type: 'danger',
                message: 'Error al subir la imagen. Intenta de nuevo.'
              };
              this.isUploading = false;
              this.uploadingImageType = null;
              this.uploadProgress = 0;
              this.cdr.detectChanges(); 
              // Ya no hacemos throw error
            },
            async () => { 
              try {
                // Obtener la URL de descarga SOLO cuando se complete
                const downloadURL = await getDownloadURL(uploadTask.snapshot.ref);
                
                // Actualizar el formulario con la URL
                this.articleForm.patchValue({
                  imageUrl: downloadURL
                });
                
                this.isUploading = false;
                this.uploadingImageType = null;
                this.uploadProgress = 0;
                this.mostrarMensaje('Imagen principal subida correctamente', 'success');
                console.log('Imagen principal subida:', downloadURL);
                this.cdr.detectChanges(); 
              } catch (getUrlError) {
                console.error('Error al obtener URL de descarga:', getUrlError);
                this.notification = {
                  type: 'danger',
                  message: 'Error al obtener URL de imagen subida.'
                };
                this.isUploading = false;
                this.uploadingImageType = null;
                this.uploadProgress = 0;
                this.cdr.detectChanges();
              }
            }
          );
          
          // Ya no necesitamos esperar aquí, el callback de completado maneja la lógica final
          // await uploadTask; 
          // const downloadURL = await getDownloadURL(snapshot.ref); <-- Movido al callback

        } catch (uploadError) { 
          console.error('Error al iniciar la subida:', uploadError);
          this.notification = {
            type: 'danger',
            message: 'No se pudo iniciar la subida de la imagen.'
          };
          this.isUploading = false;
          this.uploadingImageType = null;
          this.uploadProgress = 0;
          this.cdr.detectChanges();
        }
      } catch (error) {
        console.error('Error al procesar la imagen:', error);
        this.notification = {
          type: 'danger',
          message: 'Error al procesar la imagen'
        };
        this.isUploading = false;
        this.uploadingImageType = null;
        this.uploadProgress = 0;
      }
    }
  }

  async onContentImageSelected(event: any) {
    const file = event.target.files[0];
    const position = this.selectedPosition;
    
    if (file && position !== null && typeof position === 'number') {
      try {
        this.isUploading = true;
        this.uploadingImageType = 'paragraph'; // <-- Corregir tipo
        this.uploadProgress = 0;
        this.notification = null;

        // **Log de autenticación**
        console.log(`[onContentImageSelected - Párrafo ${position}] Verificando autenticación antes de subir:`);
        this.authService.currentUser$.subscribe(user => {
          console.log(`  currentUser$ (observable) [P${position}]:`, user);
        });
        console.log(`  currentUser (propiedad directa) [P${position}]:`, this.currentUser);
        // ******

        const reader = new FileReader();
        reader.onload = (e: any) => {
          const paragraph = this.paragraphsArray.at(position);
          if (paragraph) {
            paragraph.patchValue({
              imagePreview: e.target.result
            });
          }
        };
        reader.readAsDataURL(file);

        // Mostrar mensaje de carga
        this.mostrarMensaje('Subiendo imagen...', 'info');

        // Upload image to Firebase
        const compressedFile = await this.compressImage(file);
        const path = `articles/${Date.now()}_${file.name}`;
        const storageRef = ref(this.storage, path);
        
        try {
          // Crear la tarea de subida
          const uploadTask = uploadBytesResumable(storageRef, compressedFile);

          // Monitorear el progreso
          uploadTask.on('state_changed', 
            (snapshot) => {
              const progress = (snapshot.bytesTransferred / snapshot.totalBytes) * 100;
              this.uploadProgress = Math.round(progress);
            },
            (error) => { 
              console.error('Error durante la subida del párrafo (listener):', error);
              this.notification = {
                type: 'danger',
                message: `Error al subir imagen del párrafo ${position + 1}. Intenta de nuevo.`
              };
              this.isUploading = false;
              this.uploadingImageType = null;
              this.uploadProgress = 0;
              this.cdr.detectChanges();
              // Ya no hacemos throw error
            },
            async () => { 
              try {
                // Obtener la URL de descarga SOLO cuando se complete
                const downloadURL = await getDownloadURL(uploadTask.snapshot.ref);
                
                // Actualizar el párrafo con la URL
                const paragraph = this.paragraphsArray.at(position);
                if (paragraph) {
                  paragraph.patchValue({
                    image: downloadURL, // <-- Corregir imageUrl a image
                    imagePreview: reader.result as string // Mantener vista previa
                  });
                }
                
                this.isUploading = false;
                this.uploadingImageType = null;
                this.uploadProgress = 0;
                this.mostrarMensaje(`Imagen del párrafo ${position + 1} subida correctamente`, 'success');
                console.log(`Imagen del párrafo ${position + 1} subida:`, downloadURL);
                this.cdr.detectChanges();
              } catch (getUrlError) {
                console.error('Error al obtener URL de descarga (párrafo):', getUrlError);
                this.notification = {
                  type: 'danger',
                  message: 'Error al obtener URL de imagen de párrafo subida.'
                };
                this.isUploading = false;
                this.uploadingImageType = null;
                this.uploadProgress = 0;
                this.cdr.detectChanges();
              }
            }
          );

          // Ya no necesitamos esperar aquí
          // await uploadTask; 
          // const downloadURL = await getDownloadURL(snapshot.ref); <-- Movido al callback
          
        } catch (uploadError) { 
          console.error('Error al iniciar la subida del párrafo:', uploadError);
          this.notification = {
            type: 'danger',
            message: 'No se pudo iniciar la subida de la imagen del párrafo.'
          };
          this.isUploading = false;
          this.uploadingImageType = null;
          this.uploadProgress = 0;
          this.cdr.detectChanges();
        }
      } catch (error) {
        console.error('Error al procesar la imagen:', error);
        this.notification = {
          type: 'danger',
          message: 'Error al procesar la imagen'
        };
        this.isUploading = false;
        this.uploadingImageType = null;
        this.uploadProgress = 0;
      }
    }
  }

  getSafeUrl(url: string): SafeUrl {
    return this.sanitizer.bypassSecurityTrustUrl(url);
  }

  getSafeHtml(html: string): SafeHtml {
    // Solo usamos bypassSecurityTrustHtml porque controlamos las fuentes de HTML
    // (contenteditable con document.execCommand para formatos básicos)
    return this.sanitizer.bypassSecurityTrustHtml(html || '');
  }

  removeMainImage() {
    // Limpiar la URL y la vista previa
    this.articleForm.patchValue({
      imageUrl: '',
      imagePreview: '',
      imageCaption: ''
    });

    // Limpiar el input de archivo
    if (this.imageInput) {
      this.imageInput.nativeElement.value = '';
    }
    
    // Mostrar mensaje de éxito
    this.mostrarMensaje('Imagen principal eliminada', 'success');
    console.log('Imagen principal eliminada');
  }

  removeParagraphImage(index: number) {
    const paragraph = this.paragraphsArray.at(index);
    if (paragraph) {
      paragraph.patchValue({
        imageUrl: '',
        imagePreview: '',
        imageCaption: ''
      });

      // Limpiar el input de archivo
      if (this.contentImageInput) {
        this.contentImageInput.nativeElement.value = '';
      }

      // Mostrar mensaje de éxito
      this.mostrarMensaje('Imagen del párrafo eliminada', 'success');
      console.log('Imagen del párrafo eliminada');
    }
  }

  // Actualizar el contenido del párrafo desde el div contenteditable
  updateParagraphContent(index: number, event: Event) {
    const content = (event.target as HTMLElement).innerHTML;
    const control = this.paragraphsArray.at(index);
    control.get('content')?.setValue(content);
  }

  // Funciones para formatear texto
  openFormatToolbar(index: number) {
    this.selectedParagraphIndex = index;
    this.showFormatToolbar = true;
  }

  closeFormatToolbar() {
    this.showFormatToolbar = false;
  }

  applyFormat(format: 'bold' | 'italic') {
    if (this.selectedParagraphIndex === null) return;
    
    // Usar el comando execCommand para aplicar formato directamente
    document.execCommand(format, false);
    
    // Actualizar el valor en el FormControl con el HTML actualizado
    const editableDiv = document.querySelector(`#paragraph-${this.selectedParagraphIndex}`) as HTMLElement;
    if (editableDiv) {
      const content = editableDiv.innerHTML;
      const control = this.paragraphsArray.at(this.selectedParagraphIndex);
      control.get('content')?.setValue(content);
    }
  }

  openLinkDialog() {
    const selection = window.getSelection();
    const text = selection?.toString().trim();

    // Verificar si hay texto seleccionado
    if (!text) {
      this.mostrarMensaje('Por favor, selecciona el texto que deseas convertir en enlace', 'error');
      return;
    }

    // Guardar la selección actual
    if (selection && selection.rangeCount > 0) {
      this.selectedRange = selection.getRangeAt(0).cloneRange();
      this.selectedText = text;
      this.showLinkDialog = true;
      document.body.classList.add('modal-open');
    }
  }

  closeLinkDialog() {
    this.showLinkDialog = false;
    this.linkUrl = '';
    this.selectedText = '';
    this.selectedRange = null;
    document.body.classList.remove('modal-open');
  }

  applyLink() {
    if (!this.selectedRange || !this.linkUrl.trim()) {
      this.closeLinkDialog();
      return;
    }

    try {
      // Validar la URL
      new URL(this.linkUrl);

      // Sanitizar la URL (eliminar caracteres peligrosos)
      const sanitizedUrl = this.linkUrl.replace(/[<>"']/g, '');

      // Crear el elemento de enlace
      const linkElement = document.createElement('a');
      linkElement.href = sanitizedUrl;
      linkElement.target = '_blank';
      linkElement.rel = 'noopener noreferrer'; // Seguridad adicional para enlaces externos

      // Insertar el enlace en el rango seleccionado
      this.selectedRange.surroundContents(linkElement);

      // Actualizar el contenido del párrafo
      if (this.selectedParagraphIndex !== null) {
        const paragraphElement = document.getElementById(`paragraph-${this.selectedParagraphIndex}`);
        if (paragraphElement) {
          // Crear un evento válido
          const event = new Event('input', { bubbles: true });
          Object.defineProperty(event, 'target', { value: paragraphElement });
          this.updateParagraphContent(this.selectedParagraphIndex, event);
        }
      }

      this.mostrarMensaje('Enlace creado correctamente', 'success');
    } catch (error) {
      this.mostrarMensaje('Por favor, ingresa una URL válida', 'error');
      return;
    } finally {
      this.closeLinkDialog();
    }
  }

  // Métodos para validar y formatear URLs de video
  isYouTubeUrl(url: string): boolean {
    if (!url) return false;
    return url.includes('youtube.com') || url.includes('youtu.be');
  }

  isFacebookUrl(url: string): boolean {
    if (!url) return false;
    return url.includes('facebook.com');
  }

  // Método para previsualizar video
  previewVideo(url: string): string {
    if (!url) return '';

    if (this.isYouTubeUrl(url)) {
      // Manejar diferentes formatos de URL de YouTube
      let videoId = '';
      if (url.includes('youtube.com/watch?v=')) {
        videoId = url.split('watch?v=')[1].split('&')[0];
      } else if (url.includes('youtu.be/')) {
        videoId = url.split('youtu.be/')[1].split('?')[0];
      } else if (url.includes('youtube.com/embed/')) {
        // Ya está en formato embed, retornar tal como está
        return url;
      }
      
      if (videoId) {
        return `https://www.youtube.com/embed/${videoId}`;
      }
    } else if (this.isFacebookUrl(url)) {
      // Convertir URL de Facebook a formato embebido
      return `https://www.facebook.com/plugins/video.php?href=${encodeURIComponent(url)}`;
    }

    return url; // Devolver la URL original si no es de una plataforma conocida
  }

  // Método para eliminar el video de un párrafo
  removeParagraphVideo(index: number) {
    const paragraph = this.paragraphsArray.at(index);
    if (paragraph) {
      paragraph.get('urlVideo')?.setValue('');
      paragraph.get('pieDeVideo')?.setValue('');
    }
  }

  // Generar un extracto del contenido del artículo
  generateExcerpt(content: string): string {
    if (!content) return '';
    
    // Limpiar el HTML y obtener solo el texto
    const tempDiv = document.createElement('div');
    tempDiv.innerHTML = content;
    const textContent = tempDiv.textContent || tempDiv.innerText;
    
    // Limitar a 200 caracteres y añadir puntos suspensivos si es necesario
    const maxLength = 200;
    if (textContent.length <= maxLength) {
      return textContent;
    }
    
    // Buscar el último espacio antes del límite para no cortar palabras
    const truncated = textContent.substring(0, maxLength);
    const lastSpace = truncated.lastIndexOf(' ');
    
    return truncated.substring(0, lastSpace) + '...';
  }

  togglePreview() {
    if (this.previewMode) {
      // Saliendo de la vista previa: Restaurar estado anterior
      if (this.editorStateBeforePreview) {
        this.articleForm.patchValue(this.editorStateBeforePreview.mainForm);
        
        // Restaurar párrafos
        this.paragraphsArray.clear();
        this.editorStateBeforePreview.paragraphs.forEach((p: any) => {
          // Usar la función helper para crear el grupo
          const paragraphGroup = this.createParagraphGroup(p);
          this.paragraphsArray.push(paragraphGroup);
        });
        
        this.editorStateBeforePreview = null; // Limpiar estado guardado
      }
    } else {
      // Entrando en la vista previa: Guardar estado actual
      const currentParagraphsValue = this.paragraphsArray.value;
      // **Transformar datos guardados para que coincidan con la estructura esperada por createParagraphGroup**
      const transformedParagraphs = currentParagraphsValue.map((p: any) => ({
        title: p.title || '', // <-- AÑADIR title
        content: p.content,
        image: p.image || null, // <-- CORREGIR a p.image y default a null
        imageCaption: p.imageCaption,
        urlVideo: p.urlVideo,
        pieDeVideo: p.pieDeVideo // <-- Añadir pieDeVideo aquí
      }));

      this.editorStateBeforePreview = {
        mainForm: this.articleForm.value, 
        paragraphs: transformedParagraphs // Guardar los datos transformados
      };

      // Crear el artículo de vista previa
      const formValue = this.articleForm.value;
      this.currentArticle = {
        ...formValue,
        id: this.currentArticle?.id || 'preview',
        author: 'Vista Previa',
        authorId: 'preview',
        lastModified: new Date(),
        publishDate: formValue.status === 'published' ? new Date() : null,
        slug: this.generateSlug(formValue.title),
        excerpt: this.generateExcerpt(formValue.paragraphs?.[0]?.content || ''), // Ajustar si es necesario
        imageUrl: formValue.imageUrl || formValue.imagePreview || '',
        imageCaption: formValue.imageCaption || '',
        paragraphs: this.paragraphsArray.controls.map(control => ({
          title: control.get('title')?.value || '', // <-- Añadir title
          content: control.get('content')?.value || '',
          image: control.get('image')?.value || control.get('imagePreview')?.value || '', // Corregir 'imageUrl' a 'image'
          imageCaption: control.get('imageCaption')?.value || '',
          urlVideo: control.get('urlVideo')?.value || '', // Asegurar que el video también se incluya
          pieDeVideo: control.get('pieDeVideo')?.value || '' // <-- Añadir pieDeVideo aquí
        }))
      };
      // ** Log para depurar el objeto de vista previa **
      console.log("Entrando en modo Vista Previa. Datos generados para currentArticle:", JSON.stringify(this.currentArticle, null, 2));
      // ***********************************************
    }
    this.previewMode = !this.previewMode;
    this.cdr.detectChanges(); // Forzar detección de cambios al cambiar de modo
  }

  async logout() {
    try {
      await this.authService.logout();
      // La redirección la manejará el guard de autenticación
    } catch (error) {
      console.error('Error al cerrar sesión:', error);
      this.mostrarMensaje('Error al cerrar sesión', 'error');
    }
  }

  async deleteCurrentArticle() {
    if (!this.currentArticle || !this.currentArticle.id) {
      this.mostrarMensaje('No hay ningún artículo seleccionado para eliminar.', 'error');
      return;
    }

    // Confirmación del usuario
    const confirmation = confirm(`¿Estás seguro de que deseas eliminar el artículo "${this.currentArticle.title}"? Esta acción no se puede deshacer.`);

    if (confirmation) {
      try {
        await this.newsService.deleteArticle(this.currentArticle.id).toPromise(); // Usar el servicio
        this.mostrarMensaje('Artículo eliminado exitosamente.', 'success');
        this.createNewArticle(); // Limpiar formulario
        await this.loadArticles(); // Recargar lista de artículos
      } catch (error: any) {
        console.error('Error al eliminar el artículo:', error);
        this.mostrarMensaje(error.message || 'Error al eliminar el artículo.', 'error');
      }
    }
  }

  /**
   * Devuelve la fecha de creación real formateada para mostrar en el HTML
   */
  getCreatedAtFormatted(): string {
    if (this.currentArticle && this.currentArticle.createdAt) {
      const date = this.currentArticle.createdAt instanceof Date
        ? this.currentArticle.createdAt
        : new Date(this.currentArticle.createdAt);
      // Formato: dd/MM/yyyy HH:mm
      const day = date.getDate().toString().padStart(2, '0');
      const month = (date.getMonth() + 1).toString().padStart(2, '0');
      const year = date.getFullYear();
      const hours = date.getHours().toString().padStart(2, '0');
      const minutes = date.getMinutes().toString().padStart(2, '0');
      return `${day}/${month}/${year} ${hours}:${minutes}`;
    }
    return '';
  }
}

